import React, { useCallback, useMemo } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ComposedChart,
} from "recharts";
import { FixedSizeList as List } from "react-window";

const getPath = (x, y, width, height, borderRadius = 5) => {
    const adjustedRadius = Math.min(borderRadius, Math.abs(width) / 2, height / 2);
    return `
    M${x},${y} 
    H${x + width - Math.sign(width) * adjustedRadius} 
    A${adjustedRadius},${adjustedRadius} 0 0 ${width > 0 ? 1 : 0} ${x + width},${y + adjustedRadius} 
    V${y + height - adjustedRadius} 
    A${adjustedRadius},${adjustedRadius} 0 0 ${width > 0 ? 1 : 0} ${x + width - Math.sign(width) * adjustedRadius},${y + height} 
    H${x} 
    V${y} 
    Z
  `;
};

const CircularBar = React.memo((props) => {
    const { fill, x, y, width, height, value, unit } = props;
    const adjustedWidth = Math.abs(width);
    const isNegative = value < 0;
    // const dynamicPadding = isNegative ? Math.max(5, adjustedWidth / 2) : 2;
    return (
        <>
            <path
                d={getPath(isNegative ? x - adjustedWidth : x, y, adjustedWidth, height)}
                stroke="none"
                fill={isNegative ? "#A7002B" : fill}
            />
            <text
                // x={isNegative ? x - adjustedWidth - 5 : x + adjustedWidth + 2}
                x={isNegative ? x + 2 : x + adjustedWidth + 2}
                y={y + height / 2}
                fill="#666"
                fontSize="12"
                fontWeight={700}
                textAnchor={"start"}
                dominantBaseline="middle"
            >
                {`${value}${unit}`}
            </text>
        </>
    );
});


const CustomTooltip = React.memo(({ active, payload, label, unit }) => {
    if (active && payload && payload.length) {
        return (
            <div className="custom-tooltip">
                <div className="custom-tooltip-label">{`${label}: `}</div>
                <div className="custom-tooltip-value">{`${payload[0].value}${unit}`}</div>
            </div>
        );
    }
    return null;
});

const SingleBarChart = React.memo(({ data, isResilincGraph=false }) => {
    // const calculateMinValue = useCallback((data, keys) => {
    //     if (!Array.isArray(data)) return 0;
    //     return Math.min(
    //         ...data.map((item) =>
    //             Math.min(
    //                 ...keys.map((key) => {
    //                     const value = parseFloat(item[key]);
    //                     return isNaN(value) ? 0 : value;
    //                 })
    //             )
    //         )
    //     );
    // }, []);
    const calculateMinValue = useCallback((inputData, keys) => {
        if (!Array.isArray(inputData) || inputData.length === 0) return 0;
        
        let minValue = Infinity;
        
        for (const item of inputData) {
            for (const key of keys) {
                const value = parseFloat(item[key]);
                if (!isNaN(value) && value < minValue) {
                    minValue = value;
                }
            }
        }
        
        return minValue === Infinity ? 0 : minValue;
    }, []);

    

    const getMaxLabelWidth = (data) => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        context.font = "700 9px Arial"; // Match the Y-axis tick font style

        return Math.max(
            ...data.map((item) => context.measureText(item.name).width)
        ) + 20; // Add some padding
    };

    // In the Row component:
    // const yAxisWidth = getMaxLabelWidth(data);


    const unit = isResilincGraph?"":(data?.[0]?.unit || "MN");
    const minPrimaryValue = calculateMinValue(data, ["numericValue"]) * 2;
    // const minValue = minPrimaryValue < 0 ? minPrimaryValue : 0;
    const chartMinValue = minPrimaryValue < 0 ? minPrimaryValue : 0;


    // Calculate these values ONCE outside the Row component
    // const maxValue = useMemo(() => {
    //     return Math.max(...data.map((item) => parseFloat(item.numericValue || 0)));
    // }, [data]);
    
    // const minValue = useMemo(() => {
    //     let min = Math.min(...data.map((item) => parseFloat(item.numericValue || 0)));
    //     return min < 0 ? Math.floor(min) * 1.5 : 0;
    // }, [data]);
    const maxValue = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return 0;
        
        let max = -Infinity;
        for (const item of data) {
            const value = parseFloat(item.numericValue || 0);
            if (value > max) {
                max = value;
            }
        }
        return max === -Infinity ? 0 : max;
    }, [data]);
    
    const minValue = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return 0;
        
        let min = Infinity;
        for (const item of data) {
            const value = parseFloat(item.numericValue || 0);
            if (value < min) {
                min = value;
            }
        }
        return min < 0 ? Math.floor(min) * 1.5 : 0;
    }, [data]);




    const Row = ({ index, style }) => {
        const item = data[index];
        /*const maxValue = Math.max(...data.map((item) => parseFloat(item.numericValue || 0)));
        let minValue = Math.min(...data.map((item) => parseFloat(item.numericValue || 0)));

        // minValue = minValue < 0 ? minValue>-1? -25: Math.floor(minValue)*3.5 : 0;
        minValue = minValue < 0 ? Math.floor(minValue) * 1.5 : 0;*/
        return (
            <div style={style}>
                <ResponsiveContainer width="100%" height={50}>
                    <ComposedChart
                        layout="vertical"
                        data={[item]}
                        margin={{
                            top: 5,
                            right: 60,
                            bottom: 5,
                            left: 10,
                        }}
                    >
                        <CartesianGrid horizontal={false} vertical={false} />
                        <XAxis
                            hide={true}
                            dataKey="numericValue"
                            type="number"
                            axisLine={false}
                            tickLine={false}
                            domain={[minValue, maxValue]}
                        />
                        <YAxis
                            dataKey="name"
                            tick={{ fontSize: 9, fontWeight: 700 }}
                            type="category"
                            scale="auto"
                            axisLine={false}
                            tickLine={false}
                            width={170}
                        />
                        <Bar
                            dataKey="numericValue"
                            shape={<CircularBar unit={unit} />}
                            barSize={10}
                            fill="#0078A7"
                        />
                        <Tooltip content={<CustomTooltip unit={unit} />} />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    };
    const height = isResilincGraph?537:400
    return (
        <div style={{ width: "100%", height: height+"px" }}>
            <List
                height={height} // Height of the scrollable container
                itemCount={data.length} // Total number of items
                itemSize={40} // Height of each row
                width="100%" // Full container width
            >
                {Row}
            </List>
        </div>
    );
});

export default SingleBarChart;
